import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Button, Press, ScreenHeader, ProgressBar } from "../ui";
import { Icon } from "../Icon";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { DIMS, dimLabel, scenarioById, t } from "../content";

export default function Scenario() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const { recordScenario } = useAppState();
  const scenario = scenarioById(id);
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState(null);
  const [scores, setScores] = useState({});
  const [finished, setFinished] = useState(false);
  if (!scenario) return null;
  const s = scenario.steps[step];

  const choose = (optId) => {
    if (picked) return;
    setPicked(optId);
    const opt = s.options.find((o) => o.id === optId);
    setScores((prev) => {
      const next = { ...prev };
      Object.entries(opt.scores).forEach(([k, v]) => { next[k] = (next[k] || 0) + (v || 0); });
      return next;
    });
  };
  const next = () => {
    if (step + 1 >= scenario.steps.length) { recordScenario(scenario.id, scores); setFinished(true); return; }
    setStep(step + 1); setPicked(null);
  };

  if (finished) {
    const maxByDim = {};
    scenario.steps.forEach((st) => DIMS.forEach((d) => {
      const best = Math.max(0, ...st.options.map((o) => o.scores[d] || 0));
      maxByDim[d] = (maxByDim[d] || 0) + best;
    }));
    return (
      <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
        <ScreenHeader title={lang === "de" ? "Entscheidungsanalyse" : "Decision Analysis"} onBack={() => navigate(-1)} />
        <View style={{ padding: 20 }}>
          <Card style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: fonts.bold, fontSize: 18, color: colors.onSurface, marginBottom: 4 }}>{t(scenario.title, lang)}</Text>
            <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted }}>{lang === "de" ? "So haben deine Entscheidungen gewirkt:" : "How your decisions played out:"}</Text>
          </Card>
          {DIMS.map((d) => {
            const max = maxByDim[d] || 1;
            const val = scores[d] || 0;
            const pct = Math.round((val / max) * 100);
            const color = pct >= 70 ? colors.success : pct >= 45 ? colors.warning : colors.error;
            return (
              <Card key={d} style={{ marginBottom: 10 }}>
                <View row style={{ justifyContent: "space-between", marginBottom: 8 }}>
                  <Text style={{ fontWeight: fonts.semibold, fontSize: 14, color: colors.onSurface }}>{t(dimLabel[d], lang)}</Text>
                  <Text style={{ fontWeight: fonts.bold, fontSize: 14, color }}>{pct}%</Text>
                </View>
                <ProgressBar progress={pct} color={color} />
              </Card>
            );
          })}
          <View style={{ marginTop: 12 }}><Button title={tr("done")} iconRight="check" onPress={() => navigate(-1)} /></View>
        </View>
      </View>
    );
  }

  const chosen = picked ? s.options.find((o) => o.id === picked) : null;
  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={t(scenario.title, lang)} subtitle={`${step + 1} / ${scenario.steps.length}`} onBack={() => navigate(-1)} />
      <View style={{ padding: "8px 20px" }}><ProgressBar progress={(step / scenario.steps.length) * 100} /></View>
      <View style={{ padding: "12px 20px 120px" }}>
        <div style={{ height: 120, borderRadius: 18, overflow: "hidden", marginBottom: 16, position: "relative", backgroundImage: `url(${scenario.image})`, backgroundSize: "cover", backgroundPosition: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(rgba(15,37,64,0.1), rgba(15,37,64,0.75))", display: "flex", alignItems: "flex-end", padding: 14 }}>
            <div style={{ backgroundColor: "rgba(255,255,255,0.22)", borderRadius: 999, padding: "5px 12px" }}>
              <Text style={{ fontWeight: fonts.bold, fontSize: 12, color: "#FFFFFF" }}>{t(s.day, lang)}</Text>
            </div>
          </div>
        </div>
        <Text style={{ fontWeight: fonts.regular, fontSize: 15, lineHeight: "23px", color: colors.onSurface, marginBottom: 16 }}>{t(s.situation, lang)}</Text>
        <Text style={{ fontWeight: fonts.bold, fontSize: 17, color: colors.onSurface, marginBottom: 14 }}>{t(s.question, lang)}</Text>
        <View style={{ gap: 10 }}>
          {s.options.map((o) => {
            const isSel = picked === o.id;
            return (
              <Press key={o.id} testID={`scenario-opt-${o.id}`} onClick={() => choose(o.id)}>
                <Card style={{ padding: 14, backgroundColor: isSel ? colors.brandTertiary : colors.surface, border: `1.5px solid ${isSel ? colors.brandPrimary : colors.border}`, opacity: picked && !isSel ? 0.55 : 1 }}>
                  <Text style={{ fontWeight: fonts.medium, fontSize: 15, lineHeight: "21px", color: colors.onSurface }}>{t(o.text, lang)}</Text>
                </Card>
              </Press>
            );
          })}
        </View>
        {chosen && (
          <Card style={{ marginTop: 16, backgroundColor: colors.surfaceSecondary }}>
            <View row style={{ alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Icon name="lightbulb" size={20} color={colors.brandPrimary} weight="fill" />
              <Text style={{ fontWeight: fonts.bold, fontSize: 14, color: colors.onSurface }}>{lang === "de" ? "Auswirkung" : "Consequence"}</Text>
            </View>
            <Text style={{ fontWeight: fonts.regular, fontSize: 14, lineHeight: "21px", color: colors.onSurface }}>{t(chosen.feedback, lang)}</Text>
          </Card>
        )}
      </View>
      {picked && (
        <View style={{ position: "sticky", bottom: 0, padding: 20, backgroundColor: colors.surface, borderTop: `1px solid ${colors.divider}` }}>
          <Button testID="scenario-next" title={step + 1 >= scenario.steps.length ? (lang === "de" ? "Analyse ansehen" : "See analysis") : tr("continue")} iconRight="arrow-right" onPress={next} />
        </View>
      )}
    </View>
  );
}
